import { isLoading } from "apollo/utils";
import { FILE_DELETION_TYPES, TRACK_EVENTS } from "core/consts";
import { FileDeletionType, FileState, QueryProgress } from "core/types";
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "ds/ui";
import { getFileSource } from "dsl/ecosystems/PatientFiles/utils";
import { useTracking } from "react-tracking";
import { useTranslations } from "translations";

export type FileDeleteModalProps = {
  file?: FileState;
  handleDelete: () => void;
  isOpen?: boolean;
  mutationProgress: QueryProgress;
  reset: () => void;
};

export function FileDeleteModalv2({
  file,
  handleDelete,
  isOpen,
  mutationProgress,
  reset,
}: FileDeleteModalProps) {
  const translations = useTranslations();
  const { trackEvent } = useTracking();

  const isDeleting = isLoading(mutationProgress);
  const fileDeletionType =
    file && "fileDeletionType" in file ? file.fileDeletionType : undefined;

  const handleBack = () => {
    trackEvent({
      cta: "back",
      file_implementation: "HIS",
      modal: "delete",
      name: TRACK_EVENTS.FILE_MODAL_CTA_CLICKED,
      source: getFileSource(file),
    });
    reset();
  };

  const handleDeleteClick = () => {
    trackEvent({
      cta: "delete",
      deletion_type: fileDeletionType,
      file_implementation: "HIS",
      modal: "delete",
      name: TRACK_EVENTS.FILE_MODAL_CTA_CLICKED,
      source: getFileSource(file),
    });

    handleDelete();
  };

  const modalConfig: Record<
    FileDeletionType,
    { buttonConfirm: string; descriptions: string[]; title: string }
  > = {
    [FILE_DELETION_TYPES.EXTRACTED_ONLY]: {
      buttonConfirm:
        translations.extractDocs.filesTabChanges.deletionModal
          .buttonConfirmDeleteExtractedData,
      descriptions: [
        translations.extractDocs.filesTabChanges.deletionModal
          .descriptionExtractedDataOnly,
        translations.extractDocs.filesTabChanges.deletionModal
          .descriptionNewExtraction,
      ],
      title:
        translations.extractDocs.filesTabChanges.deletionModal
          .titleExtractedDataOnly,
    },
    [FILE_DELETION_TYPES.FILE_AND_EXTRACTED]: {
      buttonConfirm:
        translations.extractDocs.filesTabChanges.deletionModal
          .buttonConfirmDeleteDocumentAndData,
      descriptions: [
        translations.extractDocs.filesTabChanges.deletionModal
          .desriptionFileAndExtractedData,
        translations.extractDocs.filesTabChanges.deletionModal
          .descriptionNewImport,
      ],
      title:
        translations.extractDocs.filesTabChanges.deletionModal
          .titleFileAndExtractedData,
    },
    [FILE_DELETION_TYPES.FILE_ONLY]: {
      buttonConfirm: translations.fileSection.fileDeleteModal.buttonConfirm,
      descriptions: [translations.fileSection.fileDeleteModal.description],
      title: translations.fileSection.fileDeleteModal.titleNew,
    },
  };

  const config = modalConfig[fileDeletionType ?? FILE_DELETION_TYPES.FILE_ONLY];

  return (
    <Modal hideCloseButton isOpen={Boolean(isOpen)} size="lg" onClose={reset}>
      <ModalContent>
        <ModalHeader>{config.title}</ModalHeader>
        <hr className="w-full border-gray-400" />
        <ModalBody className="py-6">
          {config.descriptions.map((description, index) => (
            <p key={index} className="text-body-md text-gray-900">
              {description}
            </p>
          ))}
        </ModalBody>
        <ModalFooter>
          <div className="flex w-full justify-end gap-6">
            <Button
              color="primary"
              id="back"
              isDisabled={isDeleting}
              variant="light"
              onPress={handleBack}
            >
              {
                translations.extractDocs.filesTabChanges.deletionModal
                  .buttonCancel
              }
            </Button>
            <Button
              color="danger"
              id="delete"
              isLoading={isDeleting}
              variant="solid"
              onPress={handleDeleteClick}
            >
              {config.buttonConfirm}
            </Button>
          </div>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default FileDeleteModalv2;
